import { ServiceError } from '../errors'
import { readLocal, writeLocal } from '../local-store'
import { simulate } from '../mock-utils'
import type { DataRequests, NoticePreference, NotificacionesService, SaveDataConsent, SentNotice } from './types'

/**
 * Mock de avisos y perfil (Cierre.dc.html notif y profile), con los endpoints propuestos simulados: guarda los
 * registros completos como lo haría el backend. Códigos:
 *  - `sin-avisos` → lista vacía · `consentimiento-datos-pendiente` → consentimiento sin registrar
 *  - `preferencias-falla`, `datos-falla` → fallo al guardar · `error-notificaciones`, `offline-notificaciones`, `lento-notificaciones`
 */
const PREFS_FIELD = 'mockNoticePreferences'
const CONSENT_FIELD = 'mockSaveDataConsent'
const REQUESTS_FIELD = 'mockDataRequests'

const today = (h: number, m: number, daysAgo = 0) => {
  const d = new Date()
  d.setDate(d.getDate() - daysAgo)
  d.setHours(h, m, 0, 0)
  return d.toISOString()
}

const NOTICES = (): SentNotice[] => [
  { id: 'N1', title: 'Jorge ya se ha registrado', text: 'Ya estáis los tres. Solo falta la fianza para tener la llave.', channel: 'whatsapp', sentAt: today(10, 12), read: false },
  { id: 'N2', title: 'Recuerda reservar la fianza', text: '300 € que se apartan, no se cobran. Un minuto.', channel: 'email', sentAt: today(18, 0, 1), read: true },
  { id: 'N3', title: 'Registro completado', text: 'Tu copia firmada de las normas está en tu email.', channel: 'email', sentAt: '2026-09-14T11:30:00+02:00', read: true },
]

const DEFAULT_PREFS: NoticePreference[] = [
  { type: 'registration_pending', whatsapp: true, email: true, locked: false },
  { type: 'arrival_tomorrow', whatsapp: true, email: false, locked: false },
  { type: 'key_active', whatsapp: true, email: true, locked: true },
  { type: 'deposit_returned', whatsapp: true, email: true, locked: true },
  { type: 'extras', whatsapp: false, email: true, locked: false },
]

const EMPTY_REQUESTS: DataRequests = { export: null, deletion: null }

export const notificacionesMock: NotificacionesService = {
  async list(stay, signal) {
    await simulate(stay.guestCode, 'notificaciones', signal)
    return stay.guestCode.includes('sin-avisos') ? [] : NOTICES()
  },
  async getPreferences(stay, signal) {
    await simulate(stay.guestCode, 'notificaciones', signal)
    return readLocal<NoticePreference[]>(stay.guestCode, PREFS_FIELD) ?? DEFAULT_PREFS
  },
  async setPreference(stay, type, channel, on) {
    await simulate(stay.guestCode, 'notificaciones')
    if (stay.guestCode.includes('preferencias-falla')) throw new ServiceError('server', 'mock preference error')
    const prefs = readLocal<NoticePreference[]>(stay.guestCode, PREFS_FIELD) ?? DEFAULT_PREFS
    const target = prefs.find((p) => p.type === type)
    if (!target || target.locked) throw new ServiceError('server', 'locked preference')
    const next = prefs.map((p) => (p.type === type ? { ...p, [channel]: on } : p))
    writeLocal(stay.guestCode, PREFS_FIELD, next)
    return next
  },
  async getProfile(stay, signal) {
    await simulate(stay.guestCode, 'notificaciones', signal)
    return stay.guestCode.includes('hotel') ? { name: null, email: null, phone: null } : { name: 'Marta Ruiz García', email: 'marta.ruiz@gmail.com', phone: '+34 612 345 678' }
  },
  async getSaveDataConsent(stay, signal) {
    await simulate(stay.guestCode, 'notificaciones', signal)
    if (stay.guestCode.includes('consentimiento-datos-pendiente')) return { status: 'pending_confirmation', recordedAt: null, textVersion: null }
    const record = readLocal<SaveDataConsent & { text: string }>(stay.guestCode, CONSENT_FIELD)
    return record ? { status: record.status, recordedAt: record.recordedAt, textVersion: record.textVersion } : { status: 'granted', recordedAt: '2026-09-14T11:28:00+02:00', textVersion: '2026-09-17' }
  },
  async setSaveDataConsent(stay, input) {
    await simulate(stay.guestCode, 'notificaciones')
    if (stay.guestCode.includes('datos-falla')) throw new ServiceError('server', 'mock consent error')
    const record = { status: input.granted ? 'granted' : 'revoked', recordedAt: new Date().toISOString(), textVersion: input.textVersion, text: input.text, lang: input.lang, guestId: input.guestId } as const
    writeLocal(stay.guestCode, CONSENT_FIELD, record)
    return { status: record.status, recordedAt: record.recordedAt, textVersion: record.textVersion }
  },
  async getDataRequests(stay, signal) {
    await simulate(stay.guestCode, 'notificaciones', signal)
    return readLocal<DataRequests>(stay.guestCode, REQUESTS_FIELD) ?? EMPTY_REQUESTS
  },
  async requestExport(stay) {
    await simulate(stay.guestCode, 'notificaciones')
    if (stay.guestCode.includes('datos-falla')) throw new ServiceError('server', 'mock export error')
    const next = { ...(readLocal<DataRequests>(stay.guestCode, REQUESTS_FIELD) ?? EMPTY_REQUESTS), export: { requestedAt: new Date().toISOString(), sentTo: 'marta.ruiz@gmail.com' } }
    writeLocal(stay.guestCode, REQUESTS_FIELD, next)
    return next
  },
  async requestDeletion(stay) {
    await simulate(stay.guestCode, 'notificaciones')
    if (stay.guestCode.includes('datos-falla')) throw new ServiceError('server', 'mock deletion error')
    const next = { ...(readLocal<DataRequests>(stay.guestCode, REQUESTS_FIELD) ?? EMPTY_REQUESTS), deletion: { requestedAt: new Date().toISOString(), effectiveAt: stay.dates.checkOut } }
    writeLocal(stay.guestCode, REQUESTS_FIELD, next)
    return next
  },
}
